import {authUtil} from '../utils';

export type McpAuthorizationRequest = {
  clientName: string;
  scopes: Array<'messages:read' | 'messages:write' | 'admin:usage:read' |
    'admin:moderation:read' | 'admin:moderation:write'>;
  redirectOrigin: string;
  expiresAt: string;
};

export type McpGrant = {
  id: string;
  accountId: string;
  clientId: string;
  clientName: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt?: string;
  expiresAt: string;
};

export type McpReplyDraft = {
  id: string;
  conversationId: string;
  text: string;
  version: number;
  status: 'draft' | 'cancelled' | 'sending' | 'sent';
  expiresAt: string;
  conversation: {
    id: string;
    subject: string | null;
    type: string | null;
    participants: Array<{id: string; displayName: string | null}>;
  };
};

export class McpApiError extends Error {
  public readonly code?: string;
  public readonly status: number;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'McpApiError';
    this.status = status;
    this.code = code;
  }
}

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/mcp`;

const parseJson = async (response: Response): Promise<any> => {
  if (response.status === 204) return undefined;
  try {
    return await response.json();
  } catch {
    return undefined;
  }
};

const mcpRequest = async <T>(
  path: string,
  options: {method?: string; body?: unknown} = {}
): Promise<T> => {
  const token = await authUtil.getFreshIdToken();
  const response = await fetch(`${apiBase()}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      'br-token': token
    },
    ...(options.body === undefined
      ? {}
      : {body: JSON.stringify(options.body)})
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    throw new McpApiError(
      payload?.message ??
        payload?.error_description ??
        'The BetaReader integration request could not be completed.',
      response.status,
      payload?.code
    );
  }
  return payload as T;
};

const segment = (value: string) => encodeURIComponent(value);

export const getMcpAuthorizationRequest = (
  requestId: string,
  accountId: string
): Promise<McpAuthorizationRequest> =>
  mcpRequest(
    `/authorization-requests/${segment(requestId)}?account_id=${segment(
      accountId
    )}`
  );

const decideMcpAuthorization = (
  requestId: string,
  accountId: string,
  decision: 'approve' | 'deny'
): Promise<{redirect_uri: string}> =>
  mcpRequest(`/authorization-requests/${segment(requestId)}/${decision}`, {
    method: 'POST',
    body: {account_id: accountId}
  });

export const approveMcpAuthorization = (requestId: string, accountId: string) =>
  decideMcpAuthorization(requestId, accountId, 'approve');

export const denyMcpAuthorization = (requestId: string, accountId: string) =>
  decideMcpAuthorization(requestId, accountId, 'deny');

export const listMcpGrants = (): Promise<McpGrant[]> =>
  mcpRequest('/grants');

export const revokeMcpGrant = (grantId: string): Promise<void> =>
  mcpRequest(`/grants/${segment(grantId)}`, {method: 'DELETE'});

export const getMcpReplyDraft = (draftId: string): Promise<McpReplyDraft> =>
  mcpRequest(`/reply-drafts/${segment(draftId)}`);

export const editMcpReplyDraft = (
  draftId: string,
  text: string
): Promise<McpReplyDraft> =>
  mcpRequest(`/reply-drafts/${segment(draftId)}`, {
    method: 'PUT',
    body: {text}
  });

export const cancelMcpReplyDraft = (draftId: string): Promise<void> =>
  mcpRequest(`/reply-drafts/${segment(draftId)}`, {method: 'DELETE'});

export const sendMcpReplyDraft = (draftId: string, version: number) =>
  mcpRequest(`/reply-drafts/${segment(draftId)}/send`, {
    method: 'POST',
    body: {
      approved: true,
      version,
      idempotencyKey: `browser-${draftId}-v${version}`
    }
  });
